import React, { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { Card, Select, Typography, Spin, Empty, Row, Col } from "antd";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getRevenueByPlan } from "../../../services/dashboard.service";
import { formatCurrency } from "../../../services/utils";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
];

export default function RevenueByPlan() {
  const [months, setMonths] = useState(12);

  // Calculate start and end date based on months
  const endDate = moment().format("YYYY-MM-DD");
  const startDate = moment().subtract(months, "months").format("YYYY-MM-DD");

  const { data, isLoading } = useQuery(
    ["getRevenueByPlan", months],
    () => getRevenueByPlan({ start_date: startDate, end_date: endDate }),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { chartData, totalRevenue } = useMemo(() => {
    const rawData = data?.data || [];

    // Calculate total revenue by summing up 'total' fields
    const total = rawData.reduce(
      (acc: number, curr: any) => acc + parseFloat(curr.total || 0),
      0
    );

    // Map data for Recharts (needs 'value' key)
    const mappedData = rawData.map((item: any) => ({
      name: item.name,
      value: parseFloat(item.total),
    }));

    return { chartData: mappedData, totalRevenue: total };
  }, [data]);

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Title level={5} style={{ margin: 0 }}>
              Receita por Plano
            </Title>
            <Typography.Text type="secondary">
              Últimos {months} meses
            </Typography.Text>
          </div>
          <Select
            defaultValue={12}
            style={{ width: 120 }}
            onChange={(value) => setMonths(value)}
          >
            <Option value={6}>6 meses</Option>
            <Option value={12}>12 meses</Option>
            <Option value={24}>24 meses</Option>
          </Select>
        </div>
      }
      bordered={false}
      style={{ height: "100%" }}
    >
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
          <Spin />
        </div>
      ) : chartData.length > 0 ? (
        <Row gutter={16} align="middle">
          <Col span={24} style={{ marginBottom: 16 }}>
            <Title level={2} style={{ margin: 0 }}>
              {formatCurrency(totalRevenue)}
            </Title>
            <Text type="secondary">liquidados no período</Text>
          </Col>
          <Col span={24}>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    nameKey="name"
                  >
                    {chartData.map((entry: any, index: number) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: any) =>
                      formatCurrency(Number(value) || 0)
                    }
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Col>
        </Row>
      ) : (
        <Empty description="Sem dados para o período" />
      )}
    </Card>
  );
}
